import { useCallback, useEffect, useMemo, useState } from "react";
import { Info } from "lucide-react";
import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { Spinner } from "../../components/ui/spinner/spinner.tsx";
import { showError, showSuccess } from "../../utils/toast.ts";
import { ResignationSubmittedCard } from "../../components/features/offboarding/components/resignation-submitted-card.tsx";
import {
    RejectedStatusModal,
    ResignationFormModal,
    RetentionSentModal,
    SupportRequestBanner,
    WithdrawnStatusModal,
} from "../../components/features/offboarding/modals/index.ts";
import {
    useOffboardingDetail,
    useOffboardingInitiate,
    useOffboardingRequest,
} from "../../components/features/offboarding/hooks/index.ts";
import { useOffboardingEmployeeDisplay } from "../../components/features/offboarding/utils/use-offboarding-employee-display.ts";
import type { PageProps } from "../../types/page.ts";
import { displayFormatDate } from "../../../utils/helpers.js";
import styles from "./initiate-resignation-page.module.css";

type PipDetails = {
    startDate: string;
    endDate: string;
};

function parsePipDetails(value: unknown): PipDetails | null {
    if (value == null || typeof value !== "object") return null;

    const record = value as Record<string, unknown>;
    if (typeof record.startDate !== "string" || typeof record.endDate !== "string") {
        return null;
    }

    return {
        startDate: record.startDate,
        endDate: record.endDate,
    };
}

function parseDateOnlyMs(value: string | null | undefined): number | null {
    if (value == null || value.trim() === "") return null;

    const [datePart] = value.split("T");
    const parts = datePart.split("-").map(Number);
    if (parts.length !== 3 || parts.some((part) => !Number.isFinite(part))) {
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime())
            ? null
            : new Date(
                parsed.getFullYear(),
                parsed.getMonth(),
                parsed.getDate(),
            ).getTime();
    }

    const [year, month, day] = parts;
    return new Date(year, month - 1, day).getTime();
}

function startOfTodayMs(): number {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
}

function formatPipDate(value: string): string {
    const formatted = displayFormatDate(value);
    return formatted === "-" ? value : formatted;
}

function calculatePipDays(startDate: string, endDate: string): number | null {
    const startMs = parseDateOnlyMs(startDate);
    const endMs = parseDateOnlyMs(endDate);
    if (startMs == null || endMs == null || endMs < startMs) return null;

    return Math.round((endMs - startMs) / (24 * 60 * 60 * 1000)) + 1;
}

export default function InitiateResignationPage(_props: PageProps) {
    const { request, pip, isOnPip, viewState, offboardingId, loading, error, refetch } = useOffboardingRequest();
    const { initiate, loading: initiating, error: initiateError } = useOffboardingInitiate();
    const employee = useOffboardingEmployeeDisplay();
    const isTermination = request?.isTermination === true;
    const pipDetails = useMemo(() => parsePipDetails(pip), [pip]);
    const pipEndDateMs = parseDateOnlyMs(pipDetails?.endDate);
    const pipEndedBeforeToday =
        isOnPip && pipEndDateMs != null && pipEndDateMs < startOfTodayMs();
    const shouldShowPipWarning =
        isOnPip && pipDetails != null && !pipEndedBeforeToday;
    const shouldShowSupportBanner = viewState === "INACTIVE";
    const pipDurationDays =
        pipDetails == null
            ? null
            : calculatePipDays(pipDetails.startDate, pipDetails.endDate);

    const detailOffboardingId =
        (viewState === "RESIGNATION_IN_PROGRESS" || viewState === "EXIT_COMPLETED") &&
        offboardingId != null
            ? offboardingId
            : null;
    const {
        detail,
        loading: detailLoading,
        error: detailError,
        refetch: refetchDetail,
    } = useOffboardingDetail(detailOffboardingId);

    const [showBanner, setShowBanner] = useState(false);

    useEffect(() => {
        if (!loading) {
            setShowBanner(shouldShowSupportBanner);
        }
    }, [loading, shouldShowSupportBanner]);

    useEffect(() => {
        if (error) showError(error);
    }, [error]);

    useEffect(() => {
        if (initiateError) showError(initiateError);
    }, [initiateError]);

    useEffect(() => {
        if (detailError) showError(detailError);
    }, [detailError]);

    const handleSubmitSupport = useCallback(async () => {
        const result = await initiate(true);
        if (result) {
            setShowBanner(false);
            await refetch();
            showSuccess("Support request submitted.");
        }
    }, [initiate, refetch]);

    const handleProceedResignation = useCallback(async () => {
        const result = await initiate(false);
        if (result) {
            setShowBanner(false);
            await refetch();
        }
    }, [initiate, refetch]);

    const handleResignFromRetention = useCallback(async () => {
        const result = await initiate(false);
        if (result) {
            await refetch();
        }
    }, [initiate, refetch]);

    const handleSubmitSuccess = useCallback(async () => {
        await refetch();
    }, [refetch]);

    const handleExitInterviewSuccess = useCallback(async () => {
        await Promise.all([refetch(), refetchDetail()]);
    }, [refetch, refetchDetail]);

    const pipNotice =
        shouldShowPipWarning && pipDetails != null ? (
          <div className={styles.pipActionNotice} role="status">
           
            <div className={styles.pipActionNoticeText}>
              <p className={styles.pipActionNoticeTitle}>
                 <Info
              className={styles.pipActionNoticeIcon}
              size={18}
              strokeWidth={2}
              aria-hidden
            />
                Performance Improvement Plan (PIP) Issued
              </p>
              <p className="body-sm">
                You have been placed on a Performance Improvement Plan (PIP)
                from {formatPipDate(pipDetails.startDate)} to{" "}
                {formatPipDate(pipDetails.endDate)}
                {pipDurationDays == null ? "" : ` (${pipDurationDays} days)`}.
                Your performance will be reviewed at the end of this period,
                following which further decisions will be communicated.
              </p>
            </div>
          </div>
        ) : null;

    if (loading) {
        return (
            <PageShell>
                <Spinner variant="overlay" />
            </PageShell>
        );
    }

    return (
      <PageShell className={styles.pageRoot}>
        {initiating && <Spinner variant="overlay" />}

        {viewState === "INACTIVE" && !showBanner && shouldShowPipWarning && pipDetails != null && (
          pipNotice
        )}

        {viewState === "INACTIVE" && shouldShowSupportBanner && (
          <SupportRequestBanner
            open={showBanner}
            loading={initiating}
            notice={pipNotice}
            onSubmitSupport={() => void handleSubmitSupport()}
            onProceedResignation={() => void handleProceedResignation()}
          />
        )}

        {viewState === "RETENTION_REQUESTED" && (
          <RetentionSentModal
            open
            loading={initiating}
            onResign={() => void handleResignFromRetention()}
            updatedAt={request?.updatedAt}
          />
        )}

        {viewState === "RESIGNATION_FORM_PENDING" && offboardingId != null && (
          <ResignationFormModal
            open
            offboardingId={offboardingId}
            onSubmitSuccess={() => void handleSubmitSuccess()}
          />
        )}

        {(viewState === "RESIGNATION_IN_PROGRESS" ||
          viewState === "EXIT_COMPLETED") &&
          offboardingId != null &&
          (detailLoading || detail == null ? (
            <Spinner variant="overlay" />
          ) : viewState === "EXIT_COMPLETED" ? (
            <ResignationSubmittedCard
              employee={employee}
              offboardingId={offboardingId}
              detail={detail}
              showWithdraw={false}
              showExitInterviewPrompt
              showViewResignationForm={!isTermination}
              showAllocatedAssets
              assetsForCurrentUser
              onWithdrawSuccess={() => {}}
            />
          ) : (
            <ResignationSubmittedCard
              employee={employee}
              offboardingId={offboardingId}
              detail={detail}
              showWithdraw={!isTermination}
              showExitInterviewPrompt
              showViewResignationForm={!isTermination}
              showAllocatedAssets
              assetsForCurrentUser
              onWithdrawSuccess={() => void refetch()}
              onExitInterviewSuccess={() => void handleExitInterviewSuccess()}
            />
          ))}

        {viewState === "WITHDRAWN" && (
          <WithdrawnStatusModal
            open
            withdrawalReason={request?.withdrawalReason}
            withdrawnDate={request?.withdrawnDate ?? undefined}
            loading={initiating}
            onResign={() => void handleProceedResignation()}
            updatedAt={request?.updatedAt}
          />
        )}

        {viewState === "REJECTED" && (
          <RejectedStatusModal
            open
            approverComment={request?.approverComment}
            loading={initiating}
            onResign={() => void handleProceedResignation()}
            updatedAt={request?.updatedAt}
          />
        )}
      </PageShell>
    );
}
